import { useCallback, useEffect, useState, Fragment } from "react";
import { useParams } from "react-router-dom";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";

import Form from "./Form";
import MessageDialog from "components/__reusable/MessageDialog";
import PasswordConfirmationDialog from "components/__reusable/PasswordConfirmDialog";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import {
  getStudents,
  updateStudent,
  deleteStudent,
} from "features/student/studentAction";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import {
  getDeletionToken,
  deleteStudentWithToken,
} from "services/studentServices";
import { StudentSortValue } from "config/constants";
import type { StudentFormData } from "types/Student";
import LoadingDialog from "components/__reusable/LoadingDialog";
import type { UpdatedTrainingPicture } from "types/TrainingPicture";

const UpdateForm = () => {
  const { t } = useTranslation();
  const { classId, id } = useParams();
  const { students, loading } = useAppSelector((state) => state.student);
  const dispatch = useAppDispatch();
  const fetchStudents = () =>
    students.length === 0 &&
    dispatch(
      getStudents({
        classId: !isNaN(parseInt(classId || "")) ? parseInt(classId || "") : 0,
        sortValue: StudentSortValue.name_asc,
      })
    );
  const callFetchStudents = useCallback(fetchStudents, [
    classId,
    dispatch,
    getStudents,
  ]);
  useEffect(() => {
    callFetchStudents();
  }, [callFetchStudents]);

  const [student, setStudent] = useState<StudentFormData>({
    first_name: "",
    first_name_kata: "",
    last_name_kata: "",
    last_name: "",
    birthday: "",
    graduation_date: "",
    class_id: "",
    is_ng: false,
  });
  useEffect(() => {
    if (students.length === 0) return;
    if (isNaN(parseInt(id || ""))) return;
    const student = students.find(
      (student) => student.id === parseInt(id || "")
    );
    if (!student) return;
    setStudent({
      first_name: student.first_name,
      first_name_kata: student.first_name_kata,
      last_name_kata: student.last_name_kata,
      last_name: student.last_name,
      birthday: student.birthday,
      graduation_date: student.graduation_date,
      class_id: student.class_id.toString(),
      is_ng: student.is_ng,
    });
  }, [students, id]);

  const update = async (
    formData: StudentFormData,
    pictures: File[],
    updatedTrainingPictures: UpdatedTrainingPicture[] | undefined
  ) => {
    const updateAction = await dispatch(
      updateStudent({
        id: parseInt(id || ""),
        formData,
        trainingPictures: pictures,
        updatedTrainingPictures: updatedTrainingPictures || [],
      })
    );
    if (isFulfilled(updateAction)) {
      dispatch(
        setSuccessStatus({
          title: "_student_form._update_student_complete",
          message: "_student_form._update_student_complete_content",
        })
      );
    } else {
      dispatch(setErrorStatus(""));
    }
  };

  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteButtonClick = () => {
    setShowPasswordPrompt(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    setTokenLoading(true);
    try {
      const token = await getDeletionToken(password, "delete_student");
      localStorage.setItem("deletion_token", token);
      setShowPasswordPrompt(false);
      setShowFinalConfirmation(true);
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "パスワードが正しくありません"
        )
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPasswordPrompt(false);
    setShowFinalConfirmation(false);
    localStorage.removeItem("deletion_token");
  };

  const remove = async () => {
    const token = localStorage.getItem("deletion_token");
    if (!token) {
      dispatch(setErrorStatus("認証トークンが見つかりません"));
      setShowFinalConfirmation(false);
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteStudentWithToken(parseInt(id || ""), token);
      localStorage.removeItem("deletion_token");
      setShowFinalConfirmation(false);
      dispatch(
        setSuccessStatus({
          title: "_student_form._delete_student_success",
          message: "_student_form._delete_student_success_content",
          onClose: () => {
            window.history.back();
          },
        })
      );
    } catch (error: any) {
      setShowFinalConfirmation(false);
      dispatch(
        setErrorStatus(error.response?.data?.message || "エラーが発生しました")
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <Form isUpdate={true} student={student} onSubmit={update} />
      {loading && <LoadingDialog />}
      <div className="flex justify-end">
        <button
          className="underline font-bold text-gray-500"
          onClick={handleDeleteButtonClick}
        >
          {t("_student_form._delete_student_button")}
        </button>
      </div>
      {showPasswordPrompt && (
        <PasswordConfirmationDialog
          onCancel={handleCancel}
          onConfirm={handlePasswordConfirm}
          isLoading={tokenLoading}
          title={t("_student._delete_student", {
            defaultValue: "園児を削除する",
          })}
          message={
            <div className="space-y-3">
              <p>
                {t("_student._deletion_warning_1", {
                  defaultValue:
                    "園児を削除すると、該当園児の写真データがすべて削除され、保護者アプリからも写真が閲覧できなくなります。",
                })}
              </p>
              <p>
                {t("_student._deletion_warning_2", {
                  defaultValue:
                    "削除した園児のデータは復元することができません。",
                })}
              </p>
              <p className="pt-2">
                {t("_student._deletion_proceed_instructions", {
                  defaultValue:
                    "削除処理に進む場合は施設のパスワードを入力し、「同意して進む」を選択してください。",
                })}
              </p>
            </div>
          }
        />
      )}

      {showFinalConfirmation && (
        <MessageDialog>
          <div className="space-y-3">
            <div className="bg-red-500 text-white font-bold text-2xl text-center py-2 mb-6">
              本当に削除しますか？
            </div>
            <h2 className="text-xl font-semibold text-center whitespace-pre-wrap">
              {t("_student._delete_warning", {
                name: `${student.last_name} ${student.first_name}`,
              })}
            </h2>
            <div className="flex justify-between items-center space-x-5">
              <div className="w-1/2">
                <button
                  className="w-full text-red-500 border border-red-500 bg-white hover:bg-gray-50 py-2 px-4 rounded"
                  onClick={handleCancel}
                >
                  {t("_generic._cancel")}
                </button>
              </div>
              <div className="w-1/2">
                <button
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
                  onClick={remove}
                  disabled={deleteLoading}
                >
                  {t("_generic._delete")}
                </button>
              </div>
            </div>
          </div>
        </MessageDialog>
      )}
    </div>
  );
};

export default UpdateForm;
